import { createGuard } from "../busy.js";
import { translatePage } from "../i18n.js";
import { icon } from "../icon.js";
import { bindPopup } from "./popup.js";
import { escapeHtml } from "../escape.js";

// Hint ids only need to be unique within the document; menus re-render often
// enough that a counter is simpler than deriving one from the container.
let hintIds = 0;

// A small single-select dropdown menu: a button that shows the currently
// selected option, and a popup list to change it.
//
// This is the generalized version of the popup behavior that previously
// only existed inline in user-menu.js. As of Stage 12 Milestone 1 user-menu.js
// is a caller rather than a second copy.
//
// The popup mechanics themselves - `hidden`-attribute visibility, aria-expanded
// kept in sync with it, and outside-click/Escape listeners added on open and
// removed again on close - moved to popup.js in Stage 26 Milestone 4, so that
// the locations tab's drill-down filter menu could share them without this file
// having to grow a second layout. There is still exactly one implementation of
// opening and closing a popup in the tree; there are now two shapes built on
// it.
//
// By default the trigger label is owned by this component: it re-renders to
// the selected option's label on every selection, which is why `items`
// carries resolved label strings rather than i18n keys - callers translate
// first (labels here come from several different key namespaces).
//
// items: [{ value, label, iconName, hint }]. onSelect is called with the value only
// when it actually changes, so callers don't have to guard against
// re-selecting the current option.
//
// Returns { setActive(value) } for the one case a click cannot cover - a
// caller that has to undo a selection it turned out not to be able to honor.
// See the distance filter in locations-tab.js.
//
// An item marked `action: true` is not a selection: "Delete" is something the
// menu *does*, not a state it is now in, so `role="menuitemradio"` and
// `aria-checked` would both be lies. Action items render as plain
// `role="menuitem"`, never take the checked styling, and fire onSelect on every
// click - the "don't re-fire the current value" guard below is exactly wrong
// for them, since pressing Delete twice has to mean twice. `danger: true`
// tints one red, for the destructive one in the list. Radio and action items
// can share a menu; only the action ones opt out.
//
// An item's optional `iconName` takes the leading slot that otherwise holds
// the check mark. Where the items are things that also exist elsewhere in the
// UI with an icon - the tab bar's overflow sections - dropping the icon on the
// way into the menu makes the same destination look like a different one, so
// the icon wins the slot and the current item is marked by styling instead
// (`aria-checked` still carries it for assistive tech either way).
//
// An item's optional `hint` is a second, smaller line under its label, for a
// menu whose options are easy to mistake for one another: the locations tab's
// "New location" menu, where people after AI help with one place picked the
// several-places option because it was the only one that said AI. The hint is
// the row's accessible description (aria-describedby), not part of its name,
// so the name stays the short label a screen reader user navigates by. That
// takes an explicit aria-labelledby too: Firefox otherwise names the button
// from all of its text, hint included, and reads the hint twice.
//
// An item may be `disabled`, which renders it as a disabled <button> - it keeps
// its place in the menu rather than disappearing, so the rows do not shift
// under a finger between one opening and the next. The itinerary uses it for
// "move up" on the first entry of a day.
//
// `neutralValue` names the option that means "no choice made" (e.g. the
// locations filter's "all"). While anything else is selected, the trigger
// takes the accent color, so a collapsed icon-only trigger - whose label is
// visually hidden under 640px - still shows that a filter is narrowing the
// list. Omit it and the trigger never highlights.
//
// An item may be `reversible` (Stage 43), for the sort menus: tapping it while
// it is already the selection flips its direction instead of doing nothing.
// The item then carries `reversedLabel` beside `label`, and the row and the
// trigger show whichever one is in force - the direction is said in words
// ("Name (Z–A)"), since an arrow is read as ascending by some people and as
// "smallest on top" by others. `activeReversed` is the starting direction, and
// onSelect receives `{ reversed }` as its second argument. Selecting a
// *different* item always starts it unreversed: each order has a natural
// direction, and that is the one a new choice begins in. The checked
// reversible row carries a trailing two-way arrow as the hint that a second
// tap does something. A reversed selection counts as non-neutral for the
// tint, so a reversed default still says "not the normal order".
//
// The remaining options exist for the trip tab bar's "More" menu (Stage 09
// Milestone 6 follow-up), which is the same popup with a different shape:
//
// - `label` pins the trigger's text, instead of it tracking the selection.
//   "More" has to keep saying "More" even while Files is the open
//   section, and the *item* rows carry the which-one-is-current signal.
// - `chevron: false` drops the caret. In the tab bar the trigger is one cell
//   of five, sized like its neighbours, and the three-dot icon already says
//   "there is more here".
// - `triggerClass` replaces the default button classes, so the trigger can
//   be styled as a tab rather than as a secondary button.
// - `className` lands on the `.menu` root for variant styling (positioning
//   the dropdown, and how the current row is marked).
//
// `triggerPrefixHtml` is trusted markup placed at the head of the trigger,
// before the label. It exists for the header's user menu, whose trigger leads
// with an initials avatar rather than with an icon - `iconName` can only name
// something in the sprite. Trusted means what it says: the caller escapes
// anything user-supplied (see user-menu.js) before handing it over.
//
// Whenever the popup is open the trigger also carries
// `menu__trigger--open`, so an engaged menu reads as engaged. That applies
// to every caller, not just the tab bar - a menu you've just opened looking
// inert was never deliberate.
export function renderMenu(
  container,
  { iconName, items, activeValue, activeReversed = false, neutralValue, ariaLabel, onSelect, label, chevron = true, triggerClass = "btn btn-secondary btn-collapse", className = "", triggerPrefixHtml = "" }
) {
  let active = activeValue;
  let reversed = activeReversed;

  container.innerHTML = `
    <div class="menu${className ? ` ${className}` : ""}">
      <button type="button" class="menu__trigger${triggerClass ? ` ${triggerClass}` : ""}" data-action="toggle" aria-haspopup="menu" aria-expanded="false"${ariaLabel ? ` data-i18n-aria-label="${ariaLabel}"` : ""}>
        ${iconName ? icon(iconName) : ""}
        ${triggerPrefixHtml}
        <span class="menu__label"></span>
        ${chevron ? icon("chevron-down", { className: "menu__chevron" }) : ""}
      </button>
      <ul class="menu__dropdown" role="menu" hidden>
        ${items
          .map((item) => {
            const hintId = item.hint ? `menu-hint-${++hintIds}` : "";
            const classes = [item.action && "menu__action", item.action && item.danger && "menu__action--danger", hintId && "menu__item--hinted"].filter(Boolean).join(" ");
            return `
          <li role="none">
            <button type="button" ${item.action ? `role="menuitem"` : `role="menuitemradio" aria-checked="${item.value === active}"`}${classes ? ` class="${classes}"` : ""} data-value="${escapeHtml(item.value)}"${hintId ? ` aria-labelledby="${hintId}-label" aria-describedby="${hintId}"` : ""}${item.disabled ? " disabled" : ""}>
              ${
                item.iconName
                  ? icon(item.iconName, { className: "menu__item-icon" })
                  : // An action item with no icon gets no leading slot at all,
                    // rather than an invisible checkmark reserving space for a
                    // selection it can never carry. Mixing icon-less action
                    // items with radio ones in one menu would misalign them,
                    // which is a reason to give action items icons, not a
                    // reason for the component to fake one.
                    item.action
                    ? ""
                    : icon("check", { className: "menu__check" })
              }
              ${
                hintId
                  ? `<span class="menu__item-text"><span class="menu__item-label" id="${hintId}-label"></span><span class="menu__item-hint" id="${hintId}"></span></span>`
                  : `<span class="menu__item-label"></span>`
              }
              ${item.reversible ? icon("arrow-down-up", { className: "menu__reverse" }) : ""}
            </button>
          </li>
        `;
          })
          .join("")}
      </ul>
    </div>
  `;
  translatePage(container);

  const trigger = container.querySelector('[data-action="toggle"]');
  const dropdown = container.querySelector(".menu__dropdown");
  const labelEl = container.querySelector(".menu__label");

  const rowLabels = dropdown.querySelectorAll(".menu__item-label");
  // Set once: unlike a label, a hint never changes with the selection.
  items.forEach((item, i) => {
    if (item.hint) rowLabels[i].closest("[data-value]").querySelector(".menu__item-hint").textContent = item.hint;
  });
  syncLabel();

  // The label an item shows right now: its reversed one only while it is the
  // reversed selection.
  function labelOf(item) {
    return item.reversible && reversed && item.value === active ? item.reversedLabel : item.label;
  }

  function syncLabel() {
    // Labels are set as textContent rather than interpolated into the
    // template above so no escaping is needed for user-facing copy. Every
    // sync rather than once, since a reversible row's label changes with it.
    items.forEach((item, i) => {
      rowLabels[i].textContent = labelOf(item);
    });
    const current = items.find((item) => item.value === active);
    labelEl.textContent = label ?? (current ? labelOf(current) : "");
    trigger.classList.toggle("menu__trigger--active", neutralValue !== undefined && (active !== neutralValue || reversed));
    // Only the radio items carry a checked state; an action item has none to
    // sync, and stamping aria-checked on it would invent one.
    dropdown.querySelectorAll('[role="menuitemradio"]').forEach((btn) => {
      btn.setAttribute("aria-checked", String(btn.getAttribute("data-value") === active));
    });
  }

  // The open/close discipline, the outside-click and Escape listeners and the
  // trigger's aria-expanded now live in popup.js, so the filter menu can be a
  // different shape of popup rather than a second implementation of one. This
  // file kept every one of those behaviours; tests/ui/menu.spec.js passing
  // unchanged is what says so.
  const { close } = bindPopup(container, trigger, dropdown);

  // Most of what these menus do is a write - Delete, Duplicate, Remove, change
  // a role, change visibility - and every one of them used to be re-enterable
  // while its request was in flight. Guarding the invocation here covers all
  // twelve of those call sites at once and leaves them unchanged.
  //
  // One guard per menu rather than per item, so Edit and Remove on the same row
  // cannot race each other either. The trigger is the part that matters: close()
  // runs first, so the realistic double-fire is item, reopen, item again.
  //
  // A synchronous onSelect (a filter, a navigation) is disabled for one
  // microtask and then either navigates or re-renders, so it notices nothing.
  const guard = createGuard({ elements: () => [trigger, ...dropdown.querySelectorAll("[data-value]")] });

  dropdown.querySelectorAll("[data-value]").forEach((btn, i) => {
    btn.addEventListener("click", () => {
      const value = btn.getAttribute("data-value");
      close();
      if (items[i].action) {
        return guard.run(() => onSelect?.(value));
      }
      if (value === active) {
        if (!items[i].reversible) return;
        reversed = !reversed;
      } else {
        // A newly chosen order starts in its natural direction.
        active = value;
        reversed = false;
      }
      // Still optimistic, and still before the call: the selection is what the
      // user just chose, and a caller that cannot honor it says so through
      // setActive() below.
      syncLabel();
      return guard.run(() => onSelect?.(value, { reversed }));
    });
  });

  // Lets a caller move the selection without a click, for the case where the
  // menu proposes a state the caller then cannot enter: the locations tab's
  // distance filter needs the device's position, and if that is refused the
  // menu must fall back to "any distance" rather than claim a filter is
  // active. Returned rather than exposed as an option so a caller that never
  // needs it carries no extra API.
  return {
    setActive(value, toReversed = false) {
      if (value === active && toReversed === reversed) return;
      active = value;
      reversed = toReversed;
      syncLabel();
    },
  };
}
